import { useRouter } from "hooks/useRouter"
import { useState, useEffect, Fragment } from "react"
import UserService from "services/user.service"
import { Loader } from "common/Loader"
import { Modal } from "common/Modal"
import { formatDate } from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import { AddExpenseModal } from "components/operations"
import { ReceiptPaidStatusControl } from "components/ReceiptPaidStatusControl"
import { useSelector } from "react-redux"
import { userSelector } from "appRedux/selectors/user"
import { hasPermission } from "utils/permissions"
import { downloadFileUrl, formatMoney, myTripsPathWithPersistedTab, submissionDateDisplay } from "utils/evoTripsUi"

const service = new UserService()

export const TripDetails = () => {
  const [trip, setTrip] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [previewImage, setPreviewImage] = useState<string>("")
  const [previewFile, setPreviewFile] = useState<any>(null)
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false)
  const [showAddExpenseModal, setShowAddExpenseModal] = useState<boolean>(false)
  const [showEditExpenseModal, setShowEditExpenseModal] = useState<boolean>(false)
  const [editingExpense, setEditingExpense] = useState<any>(null)
  const [savingExpense, setSavingExpense] = useState<boolean>(false)
  const [deletingExpenseId, setDeletingExpenseId] = useState<string | null>(null)
  const [editExpenseForm, setEditExpenseForm] = useState({
    description: "",
    amount: "",
    expenseType: ""
  })
  const [updatingPaidKey, setUpdatingPaidKey] = useState<string | null>(null)
  const { successMessage, errorMessage } = useNotifications()

  const currentUser = useSelector(userSelector)
  const canEditPaid = hasPermission(currentUser, "receiptTransactions", "edit")

  const { params, push } = useRouter()
  const tripId = params.id ?? ""

  useEffect(() => {
    setLoading(true)
    service
      .getMyTripDetail(tripId)
      .then((res) => {
        setTrip(res.data.data)
      })
      .catch((err) => {
        setError(err.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [tripId])

  const isImageFile = (type: string) => {
    return type?.startsWith("image/")
  }

  const handleFileClick = (file: any) => {
    if (isImageFile(file.type)) {
      setPreviewImage(file.url)
      setPreviewFile(file)
      setShowPreviewModal(true)
    } else {
      window.open(file.url, "_blank")
    }
  }

  const handleDownload = () => {
    if (previewFile?.url) {
      downloadFileUrl(previewFile.url, previewFile.fileName || "receipt")
    }
  }

  const refreshTripDetail = async () => {
    try {
      const res = await service.getMyTripDetail(tripId)
      setTrip(res.data.data)
    } catch (err) {
      errorMessage("Failed to refresh trip data")
      console.log(err)
    }
  }

  const handleDeleteExpense = async (expense: any) => {
    const expenseId = expense?._id?.toString?.() ?? expense?._id
    if (!tripId || !expenseId) {
      errorMessage("Cannot delete this expense")
      return
    }
    if (
      !window.confirm(
        "Delete this expense and its invoice attachments? This cannot be undone."
      )
    ) {
      return
    }
    setDeletingExpenseId(String(expenseId))
    try {
      await service.deleteMyTripExpense({
        tripId,
        expenseId: String(expenseId)
      })
      successMessage("Expense deleted")
      await refreshTripDetail()
    } catch (error: any) {
      errorMessage(
        error?.response?.data?.message || "Failed to delete expense"
      )
      console.log(error)
    } finally {
      setDeletingExpenseId(null)
    }
  }

  const handleEditExpense = (expense: any) => {
    setEditingExpense(expense)
    setEditExpenseForm({
      description: expense.description || "",
      amount: expense.amount || "",
      expenseType: expense.expenseType || ""
    })
    setShowEditExpenseModal(true)
  }

  const handleUpdateExpense = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!editExpenseForm.amount) {
      errorMessage("Please fill in amount")
      return
    }

    if (isNaN(parseFloat(editExpenseForm.amount))) {
      errorMessage("Please enter a valid amount")
      return
    }

    setSavingExpense(true)

    try {
      const expenseId =
        editingExpense?._id?.toString?.() ?? String(editingExpense._id ?? "")
      const payload = {
        description: editExpenseForm.description,
        expenseId,
        tripId: tripId,
        amount: editExpenseForm.amount,
        expenseType: editExpenseForm.expenseType
      }

      await service.updateMyTripExpense(payload)
      successMessage("Expense updated successfully")

      await refreshTripDetail()
      
      // Reset form and close modal
      setEditExpenseForm({
        description: "",
        amount: "",
        expenseType: ""
      })
      setEditingExpense(null)
      setShowEditExpenseModal(false)
    } catch (error: any) {
      errorMessage(error?.response?.data?.message || "Failed to update expense")
      console.log(error)
    } finally {
      setSavingExpense(false)
    }
  }

  if (loading) {
    return <Loader />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-red-600 mb-4">Error: {error}</p>
        <button
          onClick={() => push(myTripsPathWithPersistedTab())}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Trips
        </button>
      </div>
    )
  }

  if (!trip) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-gray-600 mb-4">Trip not found</p>
        <button
          onClick={() => push(myTripsPathWithPersistedTab())}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Trips
        </button>
      </div>
    )
  }

  return (
    <Fragment>
      <div className="flex flex-col mb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="page-title">
              Trip Details
            </h1>
          </div>
          <button
            onClick={() => push(myTripsPathWithPersistedTab())}
            className="px-4 py-2  bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition"
          >
            Back to Trips
          </button>
        </div>
      </div>

      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div>
            <label className="field-label">
              Trip Name
            </label>
            <p className="mt-1 text-lg text-gray-900">{trip.name}</p>
          </div>
          <div>
            <label className="field-label">
              Badge ID
            </label>
            <p className="mt-1 text-lg text-gray-900">{trip.badgeId}</p>
          </div>
          {trip.fullName && (
            <div>
              <label className="field-label">
                Full Name
              </label>
              <p className="mt-1 text-gray-900">{trip.fullName}</p>
            </div>
          )}
          <div>
            <label className="field-label">
              Start Date
            </label>
            <p className="mt-1 text-gray-900">{formatDate(trip.startDate)}</p>
          </div>
          <div>
            <label className="field-label">
              End Date
            </label>
            <p className="mt-1 text-gray-900">{formatDate(trip.endDate)}</p>
          </div>
          <div className="">
            <label className="field-label">
              Description
            </label>
            <p className="mt-1 text-gray-900">
              {trip.description || "No description provided"}
            </p>
          </div>

          {trip.monthName && (
            <div>
              <label className="field-label">Month</label>
              <p className="mt-1 text-gray-900">{trip.monthName}</p>
            </div>
          )}
          {trip.totalDays != null && (
            <div>
              <label className="field-label">
                Total Days
              </label>
              <p className="mt-1 text-gray-900">{trip.totalDays}</p>
            </div>
          )}

          <div>
            <label className="field-label">
              Approved Trip Budget
            </label>
            <p className="mt-1 text-xl font-semibold text-blue-600">
              {formatMoney(trip.approvedTripBudget ?? trip.totalTripBudget)}
            </p>
          </div>
          <div>
            <label className="field-label">
              Total Receipt Spend
            </label>
            <p className="mt-1 text-xl font-semibold text-green-600">
              {formatMoney(trip.totalReceiptSpend ?? trip.totalAmount, "$0.00")}
            </p>
          </div>
          {trip.totalToBePaid !== undefined && trip.totalToBePaid !== null && (
            <div>
              <label className="field-label">
                Approved Amount to Pay
              </label>
              <p className="mt-1 text-xl font-semibold text-orange-600">
                {formatMoney(trip.approvedAmountToPay ?? trip.totalToBePaid)}
              </p>
            </div>
          )}
          <div>
            <label className="field-label">Submission Date</label>
            <p className={`mt-1 text-gray-900 ${!trip.submissionDate ? "text-red-600 font-medium" : ""}`}>
              {trip.submissionDate ? submissionDateDisplay(trip.submissionDate) : "Incomplete"}
            </p>
          </div>
          <div>
            <label className="field-label">Rental Car Budget per Day</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.rentalCarBudgetPerDay ?? 75)}</p>
          </div>
          <div>
            <label className="field-label">Allotted Rental Car Total</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.allottedRentalCarTotal)}</p>
          </div>
          <div>
            <label className="field-label">Flight Budget per Day</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.flightBudgetPerDay ?? 200)}</p>
          </div>
          <div>
            <label className="field-label">Allotted Flight Total ($750 max)</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.allottedFlightTotal)}</p>
          </div>
          <div>
            <label className="field-label">Hotel Budget per Day</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.hotelBudgetPerDay ?? 150)}</p>
          </div>
          <div>
            <label className="field-label">Allotted Hotel Total</label>
            <p className="mt-1 text-gray-900">{formatMoney(trip.allottedHotelTotal)}</p>
          </div>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-700">Expenses</h2>
          <button
            type="button"
            onClick={() => setShowAddExpenseModal(true)}
            className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
          >
            Add Expense
          </button>
        </div>
        {!trip.expenses || trip.expenses.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No expenses recorded for this trip
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trip.expenses.map((expense: any, index: number) => {
              const expenseId = expense._id?.toString?.() ?? String(expense._id ?? index)
              const uk = `myTrips:${tripId}:${expenseId}`
              const paid =
                expense.receiptPaidStatus === "Paid" ||
                expense.receiptPaidStatus === "Not-Paid"
                  ? expense.receiptPaidStatus
                  : "Not-Paid"
              return (
              <div
                key={expense._id || index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative"
              >
                <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                  <button
                    onClick={() => handleDeleteExpense(expense)}
                    disabled={deletingExpenseId !== null}
                    className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50 transition-colors bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Delete expense and invoice attachments"
                    type="button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleEditExpense(expense)}
                    disabled={deletingExpenseId !== null}
                    className="text-blue-500 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Edit expense"
                    type="button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                  </button>
                </div>
                <div className="flex justify-between items-start mb-3 pr-20">
                  <div className="flex-1">
                    {/* <h3 className="text-lg font-medium text-gray-900 mb-1">
                      {expense.name}
                    </h3> */}

                    <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                      {expense.expenseType && (
                        <span>
                          <span className="font-medium">Type:</span>{" "}
                          <span className="text-blue-600 font-semibold">
                            {expense.expenseType}
                          </span>
                        </span>
                      )}
                      <span>
                        <span className="font-medium">Amount:</span>{" "}
                        <span className="text-green-600 font-semibold">
                          ${parseFloat(expense.amount || 0).toFixed(2)}
                        </span>
                      </span>
                      {expense.date && (
                        <span>
                          <span className="font-medium">Date:</span>{" "}
                          {formatDate(expense.date)}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <ReceiptPaidStatusControl
                        value={paid}
                        saving={updatingPaidKey === uk}
                        disabled={!canEditPaid}
                        onChange={
                          canEditPaid
                            ? async (next) => {
                                setUpdatingPaidKey(uk)
                                try {
                                  await service.updateReceiptPaidStatus({
                                    source: "myTrips",
                                    parentId: tripId,
                                    expenseId: String(expenseId),
                                    receiptPaidStatus: next,
                                  })
                                  successMessage("Payment status updated")
                                  await refreshTripDetail()
                                } catch (e: any) {
                                  errorMessage(
                                    e?.response?.data?.message ||
                                      "Could not update payment status"
                                  )
                                } finally {
                                  setUpdatingPaidKey(null)
                                }
                              }
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </div>
                {expense.description && (
                  <p className="text-sm text-gray-600 mb-2">
                    {expense.description}
                  </p>
                )}

                {expense.files && expense.files.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                      Attachments ({expense.files.length})
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {expense.files.map((file: any) => (
                        <div
                          key={file._id}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition"
                        >
                          {isImageFile(file.type) ? (
                            <button
                              type="button"
                              onClick={() => handleFileClick(file)}
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              <span className="text-sm">{file.fileName}</span>
                            </button>
                          ) : (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                              </svg>
                              <span className="text-sm">{file.fileName}</span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
            })}
          </div>
        )}
      </div>

      <AddExpenseModal
        showModal={showAddExpenseModal}
        setShowModal={setShowAddExpenseModal}
        tripId={tripId}
        onSuccess={() => {
          refreshTripDetail()
        }}
      />

      {/* Edit Expense Modal */}
      <Modal
        showModal={showEditExpenseModal}
        setShowModal={setShowEditExpenseModal}
        title="Edit Expense"
        width="w-full md:w-1/2"
      >
        <form onSubmit={handleUpdateExpense} className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="field-label">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editExpenseForm.amount}
              onChange={(e) => setEditExpenseForm({ ...editExpenseForm, amount: e.target.value })}
              required
              className="w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
              placeholder="Enter amount"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="field-label">
              Expense Type
            </label>
            <input
              type="text"
              value={editExpenseForm.expenseType}
              onChange={(e) => setEditExpenseForm({ ...editExpenseForm, expenseType: e.target.value })}
              className="w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
              placeholder="Enter expense type"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="field-label">
              Description
            </label>
            <textarea
              value={editExpenseForm.description}
              onChange={(e) => setEditExpenseForm({ ...editExpenseForm, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"
              placeholder="Enter description"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowEditExpenseModal(false)
                setEditingExpense(null)
                setEditExpenseForm({ description: "", amount: "", expenseType: "" })
              }}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingExpense}
              className="flex-1 px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {savingExpense ? "Updating..." : "Update Expense"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        showModal={showPreviewModal}
        setShowModal={setShowPreviewModal}
        title="Image Preview"
      >
        <div className="flex flex-col items-center">
          <div className="mb-4 flex justify-end w-full">
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download
            </button>
          </div>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
      </Modal>
    </Fragment>
  )
}
